import { useMemo } from "react";
import { useCategoryItemContext } from "../../../../../services/context/CategoryItemContext";
import { useDineInMenuView } from "../../../../../services/hooks/useDineInMenuView";
import { useAppSelector } from "../../../../../services/hooks/useReduxHooks";
import { selectCartItems } from "../../../../../services/redux/selectors/cartSelectors";
import { CartItemType } from "../../../../../services/redux/types/CartLoyaltyV2Types";
import { reCheckItemDetails } from "../../../../../utils/help/itemDetails/itemDetails";
import type { ItemType } from "../../../../../utils/types";
import { RestaurantTypeEnum } from "../../../../../utils/types";
import { checkCartCount, findCartItemIndex } from "./CategoryItemHelper";
import { selectAcceptedTermsAndConditions } from "../../../../../services/redux/selectors/termsAndConditionsSelectors";

type UseCategoryItemCardReturn = {
  /** Undefined on an offer card, which has no menu item behind it. */
  itemDetails: ItemType | undefined;
  isOutOfStock: boolean;
  isItemAvailable: boolean;
  cartCount: number;
  cartItemIndex: number;
  cartItemIndexExcludingOffers: number;
  cartItem: CartItemType | undefined;
  cartItemExcludingOffers: CartItemType | undefined;
  cartCountExcludingOffers: number;
  shouldOpenBottomSheet: boolean;
  canAddToCart: boolean;
  /**
   * Static dine-in menu (prepits/project#4291): the counter is taken off the
   * card entirely rather than greyed out, the way a closed branch leaves it.
   */
  hideAddToCart: boolean;
  hidePrice: boolean;
  isEcommerce: boolean;
  isMobile: boolean;
};

export const useCategoryItemCard = (): UseCategoryItemCardReturn => {
  const { item } = useCategoryItemContext();

  const isTermsAndConditions = useAppSelector(selectAcceptedTermsAndConditions);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const cartItems = useAppSelector(selectCartItems);
  const isFranchiseOpen = useAppSelector(
    (store) => store.franchiseStatus.currentIsOpen
  );
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );
  const dineInMenuView = useDineInMenuView();

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;

  // An offer card has no menu item, so every item-derived value stays inert.
  const details = item
    ? reCheckItemDetails(
        itemStatusRedux,
        item,
        undefined,
        hasRoundingMethodAndLevel
      )
    : undefined;
  const isOutOfStock = details?.isOutOfStock || false;
  const itemDetails = details?.item;
  const isItemAvailable = item?.timeBoundAvailabilityData?.isAvailable ?? true;

  const shouldOpenBottomSheet =
    !!restaurantInfo &&
    !bottomSheetModal.isOpen &&
    (!selectedFranchise || !orderTypeParam);

  const hidePrice = franchiseConfig?.forFeatureShowcase ?? false;
  const isEcommerce = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const canAddToCart =
    !dineInMenuView &&
    !isOutOfStock &&
    !!isFranchiseOpen &&
    isTermsAndConditions &&
    isItemAvailable;

  const cartCount = useMemo(
    () => (item ? checkCartCount(item, cartItems) : 0),
    [item, cartItems]
  );
  const cartCountExcludingOffers = useMemo(
    () =>
      item ? checkCartCount(item, cartItems, { excludingOffers: true }) : 0,
    [item, cartItems]
  );
  // const totalCartCountExcludingOffers = useMemo(
  //   () => checkTotalCartCount(item, cart, { excludingOffers: true }),
  //   [item, cart]
  // );
  const cartItemIndex = useMemo(
    () => (item ? findCartItemIndex(item, cartItems) : -1),
    [item, cartItems]
  );
  const cartItemIndexExcludingOffers = useMemo(
    () =>
      item ? findCartItemIndex(item, cartItems, { excludingOffers: true }) : -1,
    [item, cartItems]
  );

  const cartItem = cartItemIndex >= 0 ? cartItems[cartItemIndex] : undefined;
  const cartItemExcludingOffers =
    cartItemIndexExcludingOffers >= 0
      ? cartItems[cartItemIndexExcludingOffers]
      : undefined;

  return {
    itemDetails,
    isOutOfStock,
    isItemAvailable,
    cartCount,
    cartCountExcludingOffers,
    cartItemIndex,
    cartItemIndexExcludingOffers,
    cartItem,
    cartItemExcludingOffers,
    shouldOpenBottomSheet,
    canAddToCart,
    hideAddToCart: dineInMenuView,
    hidePrice,
    isEcommerce,
    isMobile,
  };
};
